import { ColumnSizeToken } from '../../types/attributes';
import { AttrValue } from '../../types/attrNames';
import { VerticalType } from '../../types/cmsTypes';
import { BreakpointName } from '../../utils/createMediaQuery';
import { BrandId } from '../../utils/override';
import { tr } from '../../utils/translations/tr';
import { ComparisonTableBuilderColumn } from './ComparisonBuilderInterface';
import { BaseColumnKey } from './types';

type CardTitleStaticText = {
  card: string;
  loan: string;
  product_bka: string;
  product_stk: string;
  product_ci: string;
  product_ti: string;
};

/**
 * Resolves the label for the product/identity column of a comparison-style
 * table based on the vertical. Falls back to "Card" for credit-card and
 * unknown verticals (matches the original ComparisonTable behaviour).
 *
 * Callers must pass a pre-resolved `staticText` containing the keys above
 * (ComparisonTable + QuickAnswer translations share these).
 */
export const getCardTitle = (
  vertical: VerticalType,
  staticText: CardTitleStaticText,
): string => {
  switch (vertical) {
    case 'BANKING':
      return tr('product_bka', staticText);
    case 'INVESTMENTS':
      return tr('product_stk', staticText);
    case 'PERSONAL_LOANS':
      return tr('loan', staticText);
    case 'TRAVEL_INSURANCE':
      return tr('product_ti', staticText);
    case 'CAR_INSURANCE':
      return tr('product_ci', staticText);
    case 'CREDIT_CARD':
    case 'INSURANCE':
    case 'TELCO':
    case 'PERSONAL_FINANCE':
    default:
      return tr('card', staticText);
  }
};

export const breakPoints: BreakpointName[] = ['sm', 'md', 'lg', 'xl'];

export const responsiveTableGrids = [
  'grid-cols-[var(--table-template-cols)]',
  'md:grid-cols-[var(--table-template-cols-md)]',
  'lg:grid-cols-[var(--table-template-cols-lg)]',
  'xl:grid-cols-[var(--table-template-cols-xl)]',
];

/**
 * Returns the prefix for the specified breakpoint.
 *
 * @param {string} bp - The breakpoint for which to get the prefix.
 * @returns {string} The prefix for the breakpoint. An empty string for the first breakpoint, otherwise the breakpoint followed by a colon.
 */
export const getBpPrefix = (bp: string) => {
  return bp === breakPoints[0] ? '' : `${bp}:`;
};

export const getResponsiveTableTemplates = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ComparisonTableBuilderColumn<any>[],
) => {
  return breakPoints.reduce((prev, bp, bpIndex) => {
    const affix = bp === breakPoints[0] ? '' : `-${bp}`;
    return {
      ...prev,
      [`--table-template-cols${affix}`]: columns
        .filter((col) => !col.hiddenBreakpoints?.includes(bp))
        .map((col) => {
          const getPrevFirstDefinedBreakPoint = (
            currentBpIndex: number,
          ): string => {
            const prevColumnSize =
              col.columnSizes?.[breakPoints[currentBpIndex]];
            if (prevColumnSize) {
              return prevColumnSize;
            }
            if (currentBpIndex === 0) {
              return 'auto';
            }
            return getPrevFirstDefinedBreakPoint(currentBpIndex - 1);
          };
          return getPrevFirstDefinedBreakPoint(bpIndex) ?? 'auto';
        })
        .join(' '),
    };
  }, {});
};

/**
 * Returns an array of CSS classes to hide columns at specified breakpoints.
 *
 * @param {string[]} [hiddenBreakpoints] - An array of breakpoints at which the columns should be hidden.
 * @returns {string[]} An array of CSS classes to hide columns at specified breakpoints.
 */
export const getColumnHiddenBreakpointsClasses = (
  hiddenBreakpoints?: string[],
): string[] => {
  if (!hiddenBreakpoints?.length) {
    return [];
  }
  const lastBreakpointIndex = breakPoints.length - 1;

  const lastHiddenBreakpointIndex = Math.max(
    ...hiddenBreakpoints.map(
      (bp) => breakPoints.findIndex((b) => b === bp) ?? 0,
    ),
    0,
  );
  return [
    ...hiddenBreakpoints.map((bp) => `${getBpPrefix(bp)}hidden`),
    ...// if latest breakpoint not provided, max - xl,
    // block must be visible on greatest breakpoints
    (lastHiddenBreakpointIndex !== lastBreakpointIndex
      ? [`${breakPoints[lastHiddenBreakpointIndex + 1]}:block`]
      : []),
  ];
};

/** Column Size Token mapping to px */
export const ColumnWidthMatrix: {
  [key in 'sm' | 'md' | 'lg']: { [key in ColumnSizeToken]: string };
} = {
  sm: {
    S: '80px',
    M: '104px',
    L: '128px',
    FILL: '128px',
  },
  md: {
    S: '96px',
    M: '112px',
    L: '128px',
    FILL: '128px',
  },
  lg: {
    S: '96px',
    M: '128px',
    L: '168px',
    FILL: 'minmax(168px, 1fr)',
  },
};

export type BaseColumnType =
  | 'BANK_LOGO'
  | 'PRODUCT_NAME'
  | '$CTA'
  | 'HOT_REWARD_PICK'
  | 'BRAND_REWARD'
  | 'TRENDING_REWARD_PICK'
  | 'APPROVAL_ODDS'
  | 'GOOGLE_RATINGS';

export type ColumnType = BaseColumnType | AttrValue;

export const MapBaseColumnKeyToType = (
  vertical: VerticalType,
  key: BaseColumnKey,
): BaseColumnType => {
  switch (key) {
    case 'BANK_LOGO':
    case 'PRODUCT_NAME':
      return key;
    case 'APPLY_NOW_BUTTON':
      return '$CTA';
    case 'HOT_REWARD':
      return vertical === 'PERSONAL_LOANS'
        ? 'TRENDING_REWARD_PICK'
        : 'HOT_REWARD_PICK';
    case 'REWARD_VALUE':
      return 'BRAND_REWARD';
    case 'APPROVAL_ODDS':
      return 'APPROVAL_ODDS';
  }
};

export const VerticalColumnSizePresets: Partial<
  Record<VerticalType, Partial<Record<ColumnType, ColumnSizeToken>>>
> = {
  CREDIT_CARD: {
    PRODUCT_NAME: 'L',
    HOT_REWARD_PICK: 'M',
    BRAND_REWARD: 'M',
    EARN_RATE_PERKS: 'FILL',
    PROMO_CODE: 'FILL',
    $CTA: 'M',
  },
  PERSONAL_LOANS: {
    PRODUCT_NAME: 'L',
    ANNUAL_INTEREST_RATE: 'M',
    MONTHLY_REPAYMENT: 'M',
    APR: 'FILL',
    PROMO_CODE: 'FILL',
    $CTA: 'M',
    APPROVAL_ODDS: 'M',
    GOOGLE_RATINGS: 'L',
  },
};

export const DefaultSizeToken: ColumnSizeToken = 'M';

export const GetDefaultColumnTypes = (
  brand: BrandId,
  vertical: VerticalType,
): ColumnType[] => {
  switch (vertical) {
    case 'PERSONAL_LOANS':
      if (brand === 'moneyhero') {
        return ['MONTHLY_REPAYMENT', 'TOTAL_INTEREST', 'APR'];
      }

      return ['MONTHLY_REPAYMENT', 'ANNUAL_RATE', 'TOTAL_INTEREST', 'APR'];
    default:
      return [];
  }
};

/**
 * Whether the table would render this product at all.
 *
 * A personal loan with an empty value in one of the default columns is dropped
 * before the table renders, so the results bar has to ask the same question:
 * a count taken off the catalogue stands above fewer rows than it claims.
 * Zero counts as empty here, which is the table's own reading.
 */
export const hasRequiredColumnValues = (
  product: {
    attributes?:
      | readonly ({
          name?: { name?: string | null } | null;
          value?: unknown;
        } | null)[]
      | null;
  },
  requiredColumns: readonly string[],
): boolean =>
  !product.attributes?.some(
    (attr) =>
      !attr?.value &&
      !!attr?.name?.name &&
      requiredColumns.includes(attr.name.name),
  );

export const GetSize = (
  size: ColumnSizeToken,
  breakpoint: 'sm' | 'md' | 'lg',
) => {
  if (!ColumnWidthMatrix[breakpoint]) {
    throw new Error(`Invalid breakpoint: ${breakpoint}`);
  }
  return ColumnWidthMatrix[breakpoint][size];
};

/**
 * Get the column size in pixels
 */
export const GetColumnSizeByKey = (
  vertical: VerticalType,
  key: ColumnType,
  breakpoint: 'sm' | 'md' | 'lg',
  sizeToken: ColumnSizeToken | undefined,
) => {
  if (!sizeToken && VerticalColumnSizePresets[vertical]) {
    sizeToken = VerticalColumnSizePresets[vertical][key];
  }

  if (!sizeToken) {
    sizeToken = 'M';
  }

  return GetSize(sizeToken, breakpoint);
};

export const GetColumnSizes = (
  vertical: VerticalType,
  key: ColumnType,
  sizeToken: ColumnSizeToken | undefined,
) => ({
  sm: GetColumnSizeByKey(vertical, key, 'sm', sizeToken),
  md: GetColumnSizeByKey(vertical, key, 'md', sizeToken),
  lg: GetColumnSizeByKey(vertical, key, 'lg', sizeToken),
});
